import React, { useState } from "react";
import {
  FaBriefcase, FaMapMarkerAlt, FaCalendarAlt, FaCode, FaTrophy,
  FaLightbulb, FaChevronDown, FaExternalLinkAlt, FaCertificate,
} from "react-icons/fa";
import MediaEmbed from "./shared/MediaEmbed";
import "./Experience.css";

// ─── Static experience data ───────────────────────────────────────────────
const EXPERIENCES = [
  {
    id: "rls",
    role: "Full Stack Developer",
    company: "Rosa Luxemburg Stiftung – North Africa Office",
    period: "Oct 2025 – Present",
    location: "Tunis, Tunisia",
    type: "Full-time",
    logo: "/logos/LOGO-RLS-A-RGB.png",
    achievements: [
      "Sole developer of Voices of Change — a multilingual AI-powered storytelling platform amplifying marginalised community voices across North Africa",
      "Built a cross-lingual NLP pipeline enabling content in Arabic, French and English simultaneously",
      "Designed and implemented the full React frontend with accessible, human-centred UX",
      "Collaborated with NGO partners and regional teams on platform strategy and content governance",
      "Driving cross-cultural content and moderation workflows aligned with the foundation's human-rights mandate",
    ],
    competencies: [
      "Full-Stack Architecture", "AI/NLP Integration", "Multilingual Systems",
      "NGO & Stakeholder Collaboration", "Content Strategy", "Accessible UX",
    ],
    tech: ["React", "TypeScript", "Node.js", "Python", "NLP / AI", "Firebase", "REST APIs"],
    relatedCerts: ["Machine Learning and AI with Python", "Data Science: Building ML Models"],
    projectDescription: "Voices of Change is a multilingual AI-powered platform built for the Rosa Luxemburg Stiftung North Africa Office. It combines NLP-driven translation, content analysis, and a React frontend to surface and amplify community voices in Arabic, French, and English.",
    githubUrl: null,
    liveUrl: null,
    videoSrc: null,
    mediaLabel: null,
    presentationPdf: null,
    presentationLabel: null,
  },
  {
    id: "carbon",
    role: "Management Consultant & Data Scientist",
    company: "Carbon Insights",
    period: "Aug 2024 – Sep 2024",
    location: "Vienna, Austria (Remote)",
    type: "Internship",
    logo: "/logos/carbon insights.jpeg",
    achievements: [
      "Conducted in-depth market analysis and contributed to industry trend reports for the renewable energy sector",
      "Developed predictive models in Python to forecast renewable energy adoption patterns",
      "Delivered data-driven insights that informed strategic consulting recommendations",
      "Gained hands-on experience working in an Austrian consultancy environment across two months",
    ],
    competencies: [
      "Predictive Modelling", "Market Research", "Data Analysis",
      "Report Writing", "Renewable Energy Domain",
    ],
    tech: ["Python", "Pandas", "Scikit-Learn", "Matplotlib", "Excel"],
    relatedCerts: ["Data Science: Building ML Models", "Machine Learning with Python"],
    projectDescription: "Forecasting models for renewable energy market trends, used to inform strategic consulting reports for European energy clients.",
    githubUrl: null,
    liveUrl: null,
    videoSrc: null,
    mediaLabel: null,
    presentationPdf: null,
    presentationLabel: null,
  },
  {
    id: "highscale",
    role: "Software Engineer",
    company: "Highscale",
    period: "Jan 2024 – May 2024",
    location: "Tunis, Tunisia (Remote)",
    type: "Internship",
    logo: "/logos/highscale .png",
    achievements: [
      "Sole developer of Powerback — a dual web and mobile platform for workplace feedback, built from scratch",
      "Built a responsive React.js web app and React Native mobile app (iOS + Android) sharing a single Firebase backend",
      "Implemented real-time data sync with Firebase Firestore and complete authentication flows",
      "Delivered a production-ready cross-platform codebase within the 5-month internship timeline",
    ],
    competencies: [
      "React.js", "React Native", "Firebase", "Cross-platform Development",
      "Real-time Architecture", "Agile",
    ],
    tech: ["React.js", "React Native", "Firebase", "Firestore", "JavaScript"],
    relatedCerts: ["Certificate of Internship — Highscale"],
    projectDescription: "Powerback is a cross-platform feedback and workplace insights tool. Built as a React.js web app and React Native mobile app (iOS/Android) with a shared Firebase backend for real-time data, auth, and storage.",
    githubUrl: "https://github.com/ela2002/pb_web",
    githubUrl2: "https://github.com/ela2002/PB-mobile",
    liveUrl: null,
    videoSrc: "/experience/powerback.mp4",
    mediaLabel: "Platform demo",
    videoSrc2: "/experience/mobile.mp4",
    mediaLabel2: "Mobile app demo",
    presentationPdf: "/internships/_Graduation%20Presentation%20powerback.pdf",
    presentationLabel: "Graduation Presentation — Powerback",
  },
  {
    id: "cim",
    role: "Web Developer",
    company: "Carthage International Moving (CIM)",
    period: "2024",
    location: "Tunis, Tunisia (Remote)",
    type: "Full-time",
    logo: "/logos/logocim2.png",
    achievements: [
      "Designed and built the company's full website from the ground up",
      "Built a custom interactive volume calculator enabling customers to estimate moving costs online",
      "Integrated a Node.js / Express backend with MongoDB for inquiry storage and management",
      "Delivered a modern, responsive UI that improved the company's digital presence",
    ],
    competencies: [
      "Frontend Development", "Backend Integration", "UX Design",
      "Client Communication", "Freelance Project Management",
    ],
    tech: ["React.js", "Node.js", "Express.js", "MongoDB", "CSS"],
    relatedCerts: [],
    projectDescription: "Complete company website with a custom interactive volume calculator that lets users add furniture items and get a real-time estimate of their moving volume and cost.",
    githubUrl: null,
    liveUrl: "https://www.cim-moving.com/",
    videoSrc: null,
    mediaLabel: null,
    presentationPdf: null,
    presentationLabel: null,
  },
  {
    id: "cni",
    role: "Web Developer",
    company: "National Center of Information Technology (CNI)",
    period: "Jun 2023 – Jul 2023",
    location: "Tunis, Tunisia (Remote)",
    type: "Internship",
    logo: "/logos/cni.jpeg",
    achievements: [
      "Built a municipal asset management web application used for tracking government infrastructure",
      "Designed RESTful API with Spring Boot and connected it to a PostgreSQL relational database",
      "Implemented full CRUD operations for asset records via an Angular frontend",
      "Gained first professional experience with enterprise Java and government IT standards",
    ],
    competencies: [
      "Angular", "Spring Boot", "PostgreSQL", "REST API Design",
      "Enterprise Java", "Database Design",
    ],
    tech: ["Angular", "Spring Boot", "PostgreSQL", "Java", "Scrum"],
    relatedCerts: ["Software Engineering"],
    projectDescription: "Municipal asset management system for tracking and managing government infrastructure assets across the city, with role-based access and full CRUD operations.",
    githubUrl: "https://github.com/ela2002/gestion-biens-cni",
    liveUrl: null,
    videoSrc: "/experience/cni.mp4",
    mediaLabel: "Application demo",
    presentationPdf: "/internships/App%20GESTION%20DES%20BIENS.pdf",
    presentationLabel: "Technical Presentation — App GESTION DES BIENS",
  },
];

// ─── ExperienceMedia ──────────────────────────────────────────────────────
const ExperienceMedia = ({ videoSrc, label, videoSrc2, mediaLabel2, presentationPdf, presentationLabel }) => {
  const hasMedia = videoSrc || videoSrc2 || presentationPdf;
  if (!hasMedia) {
    return (
      <div className="exp-media-placeholder">
        <span className="exp-media-icon">📷</span>
        <span>Media coming soon — screenshots &amp; demo videos will appear here</span>
      </div>
    );
  }
  return (
    <div className="exp-media">
      {videoSrc && (
        <MediaEmbed src={videoSrc} type="video" label={label || "Demo video"} />
      )}
      {videoSrc2 && (
        <MediaEmbed src={videoSrc2} type="video" label={mediaLabel2 || "Demo video"} />
      )}
      {presentationPdf && (
        <MediaEmbed src={presentationPdf} type="pdf" label={presentationLabel || "View presentation"} />
      )}
    </div>
  );
};

// ─── ExperienceCard ───────────────────────────────────────────────────────
const ExperienceCard = ({ exp }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={`exp-card ${open ? "exp-card--open" : ""}`}>
      {/* ── Header (always visible) ──────────────────────────────────────── */}
      <button className="exp-card__header" onClick={() => setOpen(!open)}>
        <div className="exp-card__header-left">
          {exp.logo ? (
            <img src={exp.logo} alt={exp.company} className="exp-card__logo" />
          ) : (
            <div className="exp-card__logo-placeholder">
              <FaBriefcase />
            </div>
          )}
          <div>
            <div className="exp-card__role">{exp.role}</div>
            <div className="exp-card__company">{exp.company}</div>
            <div className="exp-card__meta">
              <span><FaCalendarAlt /> {exp.period}</span>
              <span><FaMapMarkerAlt /> {exp.location}</span>
              <span className="exp-card__type-badge">{exp.type}</span>
            </div>
          </div>
        </div>
        <span className="exp-card__chevron">
          <FaChevronDown />
        </span>
      </button>

      {/* ── Body (expanded) ──────────────────────────────────────────────── */}
      {open && (
        <div className="exp-card__body">
          {/* Media — shown first so it's immediately visible */}
          <ExperienceMedia
            videoSrc={exp.videoSrc}
            label={exp.mediaLabel}
            videoSrc2={exp.videoSrc2}
            mediaLabel2={exp.mediaLabel2}
            presentationPdf={exp.presentationPdf}
            presentationLabel={exp.presentationLabel}
          />

          {/* Achievements */}
          <div className="exp-section">
            <div className="exp-section__label"><FaTrophy /> Key Achievements</div>
            <ul className="exp-achievements">
              {exp.achievements.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          </div>

          {/* Competencies */}
          <div className="exp-section">
            <div className="exp-section__label"><FaLightbulb /> Skills Gained</div>
            <div className="exp-tags">
              {exp.competencies.map((c, i) => (
                <span key={i} className="exp-tag exp-tag--skill">{c}</span>
              ))}
            </div>
          </div>

          {/* Tech stack */}
          <div className="exp-section">
            <div className="exp-section__label"><FaCode /> Tech Stack</div>
            <div className="exp-tags">
              {exp.tech.map((t, i) => (
                <span key={i} className="exp-tag exp-tag--tech">{t}</span>
              ))}
            </div>
          </div>

          {/* Project */}
          <div className="exp-section">
            <div className="exp-section__label">🚀 Project</div>
            <p className="exp-project-desc">{exp.projectDescription}</p>
            {exp.githubUrl && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <a
                  href={exp.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="exp-btn exp-btn--github"
                >
                  <FaExternalLinkAlt /> {exp.githubUrl2 ? "Web app" : "View on GitHub"}
                </a>
                {exp.githubUrl2 && (
                  <a
                    href={exp.githubUrl2}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="exp-btn exp-btn--github"
                  >
                    <FaExternalLinkAlt /> Mobile app
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Live website */}
          {exp.liveUrl && (
            <div className="exp-section">
              <a
                href={exp.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="exp-btn exp-btn--live"
              >
                <FaExternalLinkAlt /> Visit Live Site
              </a>
            </div>
          )}

          {/* Related certs */}
          {exp.relatedCerts.length > 0 && (
            <div className="exp-section">
              <div className="exp-section__label"><FaCertificate /> Related Certifications</div>
              <div className="exp-tags">
                {exp.relatedCerts.map((c, i) => (
                  <span key={i} className="exp-tag exp-tag--cert">{c}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Experience ───────────────────────────────────────────────────────────
const Experience = () => (
  <div className="experience-section">
    <h2 className="section-title">Experience</h2>
    <p className="exp-intro">
      5 roles across Tunisia, Austria and the Netherlands — from intern to lead developer,
      from government infrastructure to AI-powered social platforms.
    </p>
    {EXPERIENCES.map((exp) => (
      <ExperienceCard key={exp.id} exp={exp} />
    ))}
  </div>
);

export default Experience;
